<template>
    <v-card class="scrollable-container pb-2 elevation-5">
        <v-card-title>
            <data-search-bar
                :title="title"
                :help-href="helpUrl"
                :options="dataSearchBarOptions"
                show-filters
                :is-filters-active="isFiltersActive"
                @change="onDataSearchBarChange"
                @filter="onFilterClick"
                @refresh="onRefreshClick"
            >
                <template v-slot:second-column>
                    <v-col cols="auto" class="px-5">
                        <v-sheet elevation="2" class="chart-slot pa-2">
                            <device-status-chart
                                @click="onChartClick"
                            ></device-status-chart>
                        </v-sheet>
                    </v-col>
                </template>
                <template v-slot:below-title>
                    <v-col>
                        <filter-chips
                            :items="filterChips"
                            @change="onFilterChipsChange"
                        ></filter-chips>
                    </v-col>
                </template>
                <template v-slot:disclaimer>
                    <div class="pt-1 text-body-2 font-italic">
                        Last Update {{ lastHealthPollDate }} - See Device detail
                        pages for current status
                    </div>
                </template>
            </data-search-bar>
        </v-card-title>
        <v-card-text class="scrollable-content py-2">
            <data-table-view
                v-model="selectedItems"
                :headers="tableFields"
                :items="devices"
                :sort-by="sortBy"
                :sort-desc="sortDesc"
                :page="page"
                :items-per-page="itemsPerPage"
                :loading="isDataLoading"
                :zebra-stripping="zebraStripping"
                :computed-fields="computedFields"
                :show-select="true"
                :toolbar-actions="toolbarActions"
                :selected-items="selectedItems"
                toolbar-class="mx-4"
                allow-export
                @update-sort-by="onUpdateSortBy"
                @export="onExportDataClick"
                @toolbar="onToolbarClick"
            >
                <!-- ============== Table actions template ============== -->
                <template v-slot:tableButtons="{ item }">
                    <span v-show="$can('read', 'Device')" class="px-3">
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                                <v-icon
                                    color="accent"
                                    cy-data="view-row"
                                    @click="onViewDeviceClick(item, $event)"
                                    v-on="on"
                                    >{{ icons.viewItem }}
                                </v-icon>
                            </template>
                            <span class="text-caption">Device details</span>
                        </v-tooltip>
                    </span>
                </template>
            </data-table-view>
            <v-tooltip top>
                <template v-slot:activator="{ on }">
                    <v-btn
                        v-show="
                            $can('create', 'Device') &&
                                $can('show', 'Buttons/devices/addDevice')
                        "
                        fab
                        fixed
                        right
                        bottom
                        small
                        color="primary"
                        class="mb-5 mr-1"
                        aria-label="Add new device"
                        cy-data="add-row"
                        v-on="on"
                        @click="onAddDeviceClick"
                    >
                        <v-icon>{{ icons.add }}</v-icon>
                    </v-btn>
                </template>
                <span>Add new device</span>
            </v-tooltip>
            <component
                :is="deviceDetailsComponent"
                :show-dialog="showDeviceDetailsDialog"
                :device-id="selectedDeviceId"
                @close-dialog="onCloseDeviceDetailsDialog"
                @upload="onUploadClick"
            >
            </component>
            <component
                :is="exportDialogComponent"
                title="Export to CSV: Devices"
                :entity-name="exportEntityName"
                :data-source-params="exportParams"
                :output-fields="exportFields"
                :output-file-name="exportFileName"
                @close="onExportDialogClose"
            ></component>
        </v-card-text>
        <device-filters
            v-model="showFilters"
            title="Device filters"
            :filters="filters"
            @apply="onApplyFilters"
        ></device-filters>
        <add-remove-tags ref="tagsDialog"></add-remove-tags>
        <confirmation-dialog ref="confirmationDialog"></confirmation-dialog>
        <device-upload-dialog
            :show="showUploadDialog"
            @close="onUploadDialogClose"
        ></device-upload-dialog>
    </v-card>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { isEmpty, isEmptyValues } from '@/common/helpers';
import DataSearchBar from '@/components/Data/DataSearchBar';
import DataTableView from '@/components/Data/DataTableView';
import ConfirmationDialog from '@/components/Dialogs/ConfirmationPromiseDialog';
import CsvDataExporter from '@/components/Export/CsvDataExporter';
import FilterChips from '@/components/Filters/FilterChips';
import DeviceStatusChart from './components/DeviceStatusChart';
import DeviceDetailsDialog from './components/DeviceDetailsDialog';
import DeviceFilters from './filters/DeviceFilters';
import AddRemoveTags from '../tags/AddRemoveTags';
import DeviceUploadDialog from './components/DeviceUploadDialog';

export default {
    name: 'DevicesPage',
    components: {
        'data-search-bar': DataSearchBar,
        'data-table-view': DataTableView,
        'device-status-chart': DeviceStatusChart,
        'device-details-dialog': DeviceDetailsDialog,
        'csv-data-exporter': CsvDataExporter,
        'device-filters': DeviceFilters,
        'add-remove-tags': AddRemoveTags,
        'confirmation-dialog': ConfirmationDialog,
        'filter-chips': FilterChips,
        'device-upload-dialog': DeviceUploadDialog
    },
    data() {
        return {
            title: 'Devices',
            isDataLoading: false,
            selectedDeviceId: '',
            deviceDetailsComponent: '',
            showDeviceDetailsDialog: false,
            showUploadDialog: false,
            exportDialogComponent: '',
            exportEntityName: 'devices',
            exportParams: {},
            filterChips: [],
            showFilters: false,
            lastFocusedElement: null,
            selectedItems: [],
            lastHealthPollDate: ''
        };
    },
    computed: {
        ...mapState('devices', [
            'tableFields',
            'computedFields',
            'filterFields',
            'exportFields',
            'exportFileName',
            'requestQuery',
            'responseQuery',
            'filterQuery',
            'zebraStripping',
            'helpUrl'
        ]),
        ...mapState('devices', {
            devices: 'items'
        }),
        dataSearchBarOptions() {
            return {
                page: this.requestQuery.number,
                itemsPerPage: this.requestQuery.size,
                pageCount: this.responseQuery.totalPages,
                itemsLength: this.responseQuery.totalElements,
                query: this.requestQuery.q
            };
        },
        sortBy() {
            return this.requestQuery.sort;
        },
        sortDesc() {
            return this.requestQuery.desc;
        },
        page() {
            return this.requestQuery.number;
        },
        itemsPerPage() {
            return this.requestQuery.size;
        },
        filters() {
            return this.filterQuery;
        },
        isFiltersActive() {
            return !isEmptyValues(this.filterQuery);
        },
        toolbarActions() {
            let actions = [];
            if (this.$can('show', 'Buttons/devices/addTags')) {
                actions.push({
                    name: 'tags',
                    text: 'Tags',
                    icon: 'tag',
                    tooltip: 'Add/Remove tags'
                });
            }
            if (this.$can('show', 'Buttons/devices/resetDevices')) {
                actions.push({
                    name: 'resetDevice',
                    text: 'Reset Devices',
                    icon: 'reset',
                    tooltip: 'Reset devices to factory settings'
                });
            }
            if (this.$can('show', 'Buttons/devices/rebootDevices')) {
                actions.push({
                    name: 'rebootDevice',
                    text: 'Reboot Devices',
                    icon: 'reboot',
                    tooltip: 'Turn devices off, and then on'
                });
            }
            if (this.$can('show', 'Buttons/devices/changeClosedCaption')) {
                actions.push({
                    name: 'setClosedCaptioning',
                    params: { flag: 'ON' },
                    text: 'Closed Caption On',
                    icon: 'closedCaption'
                });
            }
            if (this.$can('show', 'Buttons/devices/changeClosedCaption')) {
                actions.push({
                    name: 'setClosedCaptioning',
                    params: { flag: 'OFF' },
                    text: 'Closed Caption Off',
                    icon: 'closedCaptionOff'
                });
            }
            if (this.$can('show', 'Buttons/devices/changeTalkingGuide')) {
                actions.push({
                    name: 'setTalkingGuide',
                    params: { flag: 'ON' },
                    text: 'Talking Guide On',
                    icon: 'talkingGuide'
                });
            }
            if (this.$can('show', 'Buttons/devices/changeTalkingGuide')) {
                actions.push({
                    name: 'setTalkingGuide',
                    params: { flag: 'OFF' },
                    text: 'Talking Guide Off',
                    icon: 'talkingGuideOff'
                });
            }
            if (this.$can('show', 'Buttons/devices/deleteDevices')) {
                actions.push({
                    name: 'deleteByCriteria',
                    text: 'Delete',
                    icon: 'delete',
                    tooltip: 'Delete devices',
                    color: 'red accent-4'
                });
            }
            return actions;
        }
    },
    async created() {
        if (this.tableFields && !this.tableFields.length) {
            await this.setupView();
            await this.resetRequestQuery();
        }
    },
    async mounted() {
        this.isDataLoading = true;
        setTimeout(() => {
            this.findDevices(this.getInitialPageParams());
            this.getLastHealthPollDate();
            this.fetchDeviceTags();
            this.fetchSpaceTags();
        }, 200);
        this.fetchDeviceOptions();
    },
    methods: {
        // =================== Vuex Actions =====================
        ...mapActions('devices', [
            'fetchDevices',
            'setupView',
            'resetRequestQuery',
            'postBulkActions',
            'fetchLastHealthPollDate'
        ]),
        ...mapActions('device', ['fetchDeviceOptions']),
        ...mapActions('tags', ['fetchDeviceTags', 'fetchSpaceTags']),
        // ===================== Events ========================
        onDataSearchBarChange(options) {
            this.findDevices(
                {
                    ...options,
                    sortBy: this.sortBy,
                    sortDesc: this.sortDesc
                },
                this.filterQuery
            );
        },
        onUpdateSortBy(sortOptions) {
            if (sortOptions) {
                this.findDevices(
                    {
                        ...this.dataSearchBarOptions,
                        ...sortOptions
                    },
                    this.filterQuery
                );
            }
        },
        onAddDeviceClick(event) {
            this.deviceDetailsComponent = 'device-details-dialog';
            this.selectedDeviceId = '';
            this.showDeviceDetailsDialog = true;
            this.lastFocusedElement = event.target;
        },
        onViewDeviceClick(item, event) {
            this.deviceDetailsComponent = 'device-details-dialog';
            this.selectedDeviceId = item.id;
            this.showDeviceDetailsDialog = true;
            this.lastFocusedElement = event.target;
        },
        onCloseDeviceDetailsDialog(refreshData) {
            // if refreshData then we need to refresh the current data.
            this.deviceDetailsComponent = '';
            this.showDeviceDetailsDialog = false;
            this.selectedDeviceId = '';
            if (refreshData) {
                this.findDevices(this.getInitialPageParams());
            }
            this.setNextFocus(this.lastFocusedElement);
        },
        onExportDataClick(event) {
            const searchParams = {
                number: 1,
                size:
                    this.dataSearchBarOptions.itemsLength <= 10000
                        ? this.dataSearchBarOptions.itemsLength
                        : 10000,
                q: this.dataSearchBarOptions.query,
                sort: this.sortBy,
                desc: this.sortDesc
            };
            const filterParams = this.filterQuery;
            this.exportParams = {
                searchParams,
                filterParams
            };
            this.exportDialogComponent = 'csv-data-exporter';
            this.lastFocusedElement = event.target;
        },
        onExportDialogClose() {
            this.exportDialogComponent = '';
            this.setNextFocus(this.lastFocusedElement);
        },
        onFilterClick() {
            this.showFilters = true;
        },
        onApplyFilters({ filters, filtersDesc }) {
            this.applyFilters(filters, filtersDesc);
        },
        onFilterChipsChange(filtersDesc) {
            if (filtersDesc) {
                let newFilters = {};
                filtersDesc.forEach(item => {
                    newFilters[item.name] = item.value;
                });
                this.applyFilters(newFilters, filtersDesc);
            }
        },
        onChartClick({ label }) {
            this.applyStatusFilter({ healthCheckStatus: label });
        },
        async onToolbarClick({ item, selected }) {
            const action = item.name;
            const criteria = { ids: selected.map(item => item.id) };
            const params = item.params || {};
            let needsRefresh = false;
            if (item.name === 'deleteByCriteria') {
                const res = await this.$refs.confirmationDialog.open(
                    'Delete Devices',
                    'Are you sure you want to delete the selected devices?',
                    '',
                    { showIcon: true }
                );
                if (res) {
                    await this.postBulkActions({ action, criteria });
                    needsRefresh = true;
                }
            } else if (item.name === 'tags') {
                // get all tags for the selected items
                let tagsFilter = [];
                selected.forEach(item => {
                    if (item.vmsTags && item.vmsTags.length) {
                        tagsFilter.push(...item.vmsTags.map(tag => tag.name));
                    }
                });
                const res = await this.$refs.tagsDialog.open(
                    'Add/Remove tags for selected devices',
                    'device',
                    tagsFilter
                );
                if (res && res.added && res.added.length) {
                    await this.postBulkActions({
                        action: 'addTagsByCriteria',
                        criteria,
                        params: res.added
                    });
                    needsRefresh = true;
                }
                if (res && res.removed && res.removed.length) {
                    await this.postBulkActions({
                        action: 'removeTagsByCriteria',
                        criteria,
                        params: res.removed
                    });
                    needsRefresh = true;
                }
            } else {
                await this.postBulkActions({ action, criteria, params });
                needsRefresh = true;
            }
            if (needsRefresh) {
                // need to wait for DS search to refresh the data
                setTimeout(() => {
                    this.refreshView();
                }, 1500);
                this.selectedItems = [];
            }
        },
        onUploadClick() {
            this.showUploadDialog = true;
        },
        onUploadDialogClose(refreshParentData) {
            this.showUploadDialog = false;
            if (refreshParentData) {
                this.findDevices(this.getInitialPageParams());
            }
            this.setNextFocus(this.lastFocusedElement);
        },
        onRefreshClick() {
            this.refreshView();
        },
        // ===================== Others ========================
        refreshView() {
            this.findDevices(
                {
                    ...this.dataSearchBarOptions,
                    sortBy: this.sortBy,
                    sortDesc: this.sortDesc
                },
                this.filterQuery
            );
            this.getLastHealthPollDate();
        },
        applyFilters(filters, filtersDesc) {
            this.filterChips = filtersDesc;
            this.findDevices(
                {
                    ...this.dataSearchBarOptions,
                    page: 1,
                    sortBy: this.sortBy,
                    sortDesc: this.sortDesc
                },
                filters
            );
        },
        async findDevices(searchOptions, filterOptions) {
            this.isDataLoading = true;
            try {
                if (!isEmpty(searchOptions)) {
                    const searchParams = {
                        number: searchOptions.page,
                        size: searchOptions.itemsPerPage,
                        q: searchOptions.query,
                        sort: searchOptions.sortBy,
                        desc: searchOptions.sortDesc
                    };
                    const filterParams = { ...filterOptions };
                    await this.fetchDevices({ searchParams, filterParams });
                }
            } finally {
                this.isDataLoading = false;
            }
        },
        getInitialPageParams() {
            return {
                page: 1,
                itemsPerPage: this.itemsPerPage,
                query: this.requestQuery.q,
                sortBy: this.sortBy,
                sortDesc: this.sortDesc
            };
        },
        applyStatusFilter({ healthCheckStatus }) {
            if (healthCheckStatus !== this.filters.healthCheckStatus) {
                this.filterChips = [
                    {
                        name: 'healthCheckStatus',
                        text: 'Health',
                        value: healthCheckStatus
                    }
                ];
                this.findDevices(
                    {
                        ...this.dataSearchBarOptions,
                        sortBy: this.sortBy,
                        sortDesc: this.sortDesc
                    },
                    {
                        healthCheckStatus
                    }
                );
            }
        },
        setNextFocus(sourceElement) {
            const keydownEsc = new KeyboardEvent('keydown', { keyCode: 27 });
            this.$nextTick(() => {
                if (sourceElement) {
                    const button = sourceElement.closest('button');
                    button.focus();
                    button.dispatchEvent(keydownEsc);
                }
            });
        },
        getLastHealthPollDate() {
            this.fetchLastHealthPollDate({}).then(res => {
                if (res) {
                    this.lastHealthPollDate = new Date(res).toLocaleString();
                }
            });
        }
    }
};
</script>

<style lang="scss" scoped></style>
